import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import axios from '~/data/http/axios'
import i18n from '~/i18n'
import Alert from '~/Common-Objects/UI/Alert'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import UpsellModal from '~/Common-Objects/UpsellModal'
import { useIsTranspond } from '~/Common-Objects/hooks/siteMaster'
import * as siteMaster from '~/data/actions/siteActions'
import { fetchTier } from '~/data/actions/accountActions'
import TranspondLogo from './transpond.svg'
import CapabilityTeaser from './CapabilityTeaser'
import ExamplePrompts from './ExamplePrompts'
import PlatformInstructions from './PlatformInstructions'

const LAB_NAME = 'transpond-mcp'

function BackLink({ onBack }) {
    return (
        <button type='button' className='mcp-back' onClick={onBack}>
            <Icons icon='arrowLeft' style={{ height: 14, width: 14 }} />
            {i18n.t('mcp:back')}
        </button>
    )
}

function Hero() {
    return (
        <div className='mcp-hero'>
            <div className='mcp-hero__mark'>
                <img src={TranspondLogo} alt='' width={48} height={48} />
            </div>
            <div className='mcp-hero__copy'>
                <div className='mcp-hero__eyebrow'>{i18n.t('mcp:eyebrow')}</div>
                <h1 className='mcp-hero__title'>{i18n.t('mcp:title')}</h1>
                <p className='mcp-hero__lead'>{i18n.t('mcp:lead')}</p>
            </div>
        </div>
    )
}

function GatePanel({ title, body, cta, onCta, secondaryCta, onSecondary, ctaDisabled, children }) {
    return (
        <div className='mcp-gate'>
            <h2 className='mcp-gate__title'>{title}</h2>
            <p className='mcp-gate__body'>{body}</p>
            {children}
            <div className='mcp-gate__actions'>
                {cta && <Button className='button-primary' text={cta} onClick={onCta} disabled={ctaDisabled} />}
                {secondaryCta && <Button text={secondaryCta} onClick={onSecondary} disabled={ctaDisabled} />}
            </div>
        </div>
    )
}

export default function TranspondMcp() {
    const dispatch = useDispatch()
    const history = useHistory()
    const isTranspond = useIsTranspond()
    const account = useSelector((state) => state.accountMaster)

    const [labEnabled, setLabEnabled] = useState(!!account.accountMaster?.beta?.[LAB_NAME])
    const [mcpAllowed, setMcpAllowed] = useState(null)
    const [requiredTier, setRequiredTier] = useState(null)
    const [enablingLab, setEnablingLab] = useState(false)

    useEffect(() => {
        setLabEnabled(!!account.accountMaster?.beta?.[LAB_NAME])
    }, [account.accountMaster?.beta])

    useEffect(() => {
        let cancelled = false
        axios
            .get('/accountMaster/tier/mcp')
            .then((res) => {
                if (!cancelled) setMcpAllowed(!!res.data.allowed)
            })
            .catch(() => {
                if (!cancelled) setMcpAllowed(false)
            })
        axios
            .get('/accountMaster/plan/requiredTier?feature=mcp')
            .then((res) => {
                if (!cancelled) setRequiredTier(res.data)
            })
            .catch(() => {})
        return () => {
            cancelled = true
        }
    }, [])

    function goBack() {
        history.push('/cp/company/integrations')
    }

    function markLabEnabled() {
        setLabEnabled(true)
        const next = {
            ...account.accountMaster,
            beta: { ...(account.accountMaster.beta || {}), [LAB_NAME]: true }
        }
        dispatch({
            type: 'CHANGE_ACCOUNTMASTER',
            payload: { AccountMaster: next }
        })
    }

    async function enableLab() {
        if (enablingLab) return
        setEnablingLab(true)
        try {
            const response = await axios.get('/accountmaster/labs')
            const lab = (response.data || []).find((entry) => entry.name === LAB_NAME)
            if (!lab) {
                history.push('/cp/labs')
                return
            }
            await axios.put('/accountmaster/labs', { betaId: lab.id })
            markLabEnabled()
        } catch (err) {
            history.push('/cp/labs')
        } finally {
            setEnablingLab(false)
        }
    }

    function showUpsell() {
        dispatch(
            siteMaster.alterFullpage(
                <UpsellModal
                    feature='mcp'
                    requiredTier={requiredTier?.name || 'Growth'}
                    onUpgrade={() => {
                        dispatch(siteMaster.alterFullpage(null))
                        dispatch(fetchTier())
                        axios.get('/accountMaster/tier/mcp').then((res) => {
                            setMcpAllowed(!!res.data.allowed)
                        })
                    }}
                />
            )
        )
    }

    if (!isTranspond) {
        return (
            <div className='mcp-page'>
                <Alert type='info' content={i18n.t('mcp:agencyOnly')} />
                <Button className='mar-t15' text={i18n.t('mcp:back')} onClick={goBack} />
            </div>
        )
    }

    return (
        <div className='mcp-page'>
            <BackLink onBack={goBack} />
            <Paper className='mcp-shell'>
                <Hero />

                {!labEnabled && mcpAllowed === null && <LoadingIcons iconType='2' />}

                {!labEnabled && mcpAllowed !== null && (
                    <GatePanel
                        title={i18n.t(mcpAllowed ? 'mcp:lab.title' : 'mcp:lab.upgradeTitle')}
                        body={
                            mcpAllowed
                                ? i18n.t('mcp:lab.body')
                                : i18n.t('mcp:lab.upgradeBody', {
                                      tier: requiredTier?.name || i18n.t('mcp:lab.defaultTier')
                                  })
                        }
                        cta={enablingLab ? i18n.t('mcp:lab.enabling') : i18n.t('mcp:lab.cta')}
                        onCta={enableLab}
                        ctaDisabled={enablingLab}
                        secondaryCta={i18n.t('mcp:lab.fallback')}
                        onSecondary={() => history.push('/cp/labs')}
                    >
                        <CapabilityTeaser />
                    </GatePanel>
                )}

                {labEnabled && mcpAllowed === false && (
                    <GatePanel
                        title={i18n.t('mcp:upgrade.title')}
                        body={i18n.t('mcp:upgrade.body')}
                        cta={i18n.t('mcp:upgrade.cta')}
                        onCta={showUpsell}
                    >
                        <CapabilityTeaser />
                    </GatePanel>
                )}

                {labEnabled && mcpAllowed === null && <LoadingIcons iconType='2' />}

                {labEnabled && mcpAllowed === true && <PlatformInstructions />}
            </Paper>

            {labEnabled && mcpAllowed === true && (
                <Paper className='mcp-examples-shell'>
                    <ExamplePrompts />
                </Paper>
            )}
        </div>
    )
}
